import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { GET_USER_LIST_MASSAGES } from "src/graphql/query";
import {
  Box,
  Checkbox,
  debounce,
  Grid,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import ActionMenu from "../Business/ActionMenu";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModel from "src/components/Common/DeleteModel";
import { DELETE_SPIN_REWARD } from "src/graphql/mutations";
import { toast } from "react-toastify";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import SendBulkMessagesModal from "./SendBulkMessagesModal";
import { MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router";
import { ROUTES } from "src/constant";
import { useQuery } from "@apollo/client";
import VerifiedIcon from '@mui/icons-material/Verified';
interface SendBulkMessages {
  _id: string;
}

const SendBulkMessages: React.FC = () => {
  const qrRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] = useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 100 }); // Changed page to 0 for Material-UI pagination
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [selectedSpinReward, setSelectedSpinReward] = useState<SendBulkMessages | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const initialSendMessagesObj = {
    type: "",
    userType: "",
    businessType: "",
    emailBody: "",
    emailSubject: "",
    imageUrl: "",
    businessClaimType: "",
    personalEmailId: "",
    messageText: "",
    personalNumber: "",
    userList: [],
  };
  const [sendMessagesObj, setSendMessagesObj] = useState<any>(initialSendMessagesObj);
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>(["BUSINESS"]);
  const [businessClaimType, setBusinessClaimType] = useState<boolean | undefined>(true);
  const navigate = useNavigate();

  const COLUMNS = [
    {
      id: "index",
      label: "#",
      width: "5%",
      render: (row: any) => <Typography>{row?.index + 1}</Typography>
    },
    ...(isSuperAdmin
      ? [
        {
          id: "select",
          label: (
            <Checkbox
              checked={selectedIndexes.length === list.length && list.length > 0}
              indeterminate={selectedIndexes.length > 0 && selectedIndexes.length < list.length}
              onChange={(event) => handleSelectAll(event.target.checked)}
            />
          ),
          render: (row: any) => (
            <Checkbox
              checked={selectedIndexes.includes(row.index)}
              onChange={() => handleRowSelect(row.index)}
            />
          ),
          width: "10%",
        },
      ]
      : []),
    {
      id: "name",
      label: "Business / User Name",
      width: "30%",
      render: (row: any) =>
        <Box display="flex" alignItems="center">
          <Typography>{row?.name || row?.businessName || "N/A"}</Typography>
          {businessClaimType && (
            <Tooltip title="Verified" placement="top">
              <VerifiedIcon
                sx={{
                  fontSize: '16px',
                  color: '#1DA1F2',
                  ml: 1,
                }}
              />
            </Tooltip>
          )}
        </Box>
      ,
    },
    {
      id: "email",
      label: "Email",
      width: "30%",
      render: (row: any) => <Typography>{row?.email || "N/A"}</Typography>,
    },
    {
      id: "mobileNumber",
      label: "Mobile Number",
      width: "15%",
      render: (row: any) => <Typography>{row?.mobileNumber || "N/A"}</Typography>,
    },
    {
      id: "userType",
      label: "User Type",
      width: "15%",
      render: (row: any) => <Typography>{row?.userType || "N/A"}</Typography>,
    },
  ];

  const { loading, error, data, refetch } = useQuery(GET_USER_LIST_MASSAGES, {
    variables: {
      page: pagination.page + 1, // Adjusting for 0-based index in UI vs 1-based in API
      limit: pagination.pageSize,
      userType: selectedUserTypes,
      businessClaimType,
    },
  });

  useEffect(() => {
    if (data?.getUserListForMassages?.data) {
      const updatedList = data?.getUserListForMassages?.data?.map((item: any, index: number) => ({
        ...item,
        index,
      }));
      setList(updatedList);
      setTotal(data?.getUserListForMassages?.total || 0);
    }
  }, [data]);

  const handleRowSelect = (index: number) => {
    const selectedItem = list.find((item: any) => item.index === index);
    if (!selectedItem) return;

    setSelectedIndexes((prevSelected) => {
      const isSelected = prevSelected.includes(index);
      const newIndexes = isSelected
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index];

      // Update selectedRows at the same time
      setSelectedRows((prevRows) => {
        const exists = prevRows.some((row: any) => row.index === index);
        return exists
          ? prevRows.filter((row: any) => row.index !== index)
          : [...prevRows, selectedItem];
      });

      return newIndexes;
    });
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allIndexes = list.map((item: any) => item.index);
      setSelectedIndexes(allIndexes);
      setSelectedRows(list);
    } else {
      setSelectedIndexes([]);
      setSelectedRows([]);
    }
  };

  useEffect(() => {
    const validIndexes = list.map((item: any) => item.index);
    setSelectedRows((prev) => prev.filter((row: any) => validIndexes.includes(row.index)));
    setSelectedIndexes((prev) => prev.filter((idx) => validIndexes.includes(idx)));
  }, [list]);

  const debouncedRefetch = useCallback(
    debounce((term: string, selectedTypes: string[]) => {
      setSearchTerm(term.length > 0 ? term : "");
      refetch({
        userType: selectedTypes,
        businessClaimType,
        search: term,
        page: 1, // Reset to first page when searching
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.pageSize]
  );

  const handleCloseModal = () => {
    setSendMessagesObj(initialSendMessagesObj);
    setOpenModal(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 0 })); // Reset to first page when searching
    debouncedRefetch(value, selectedUserTypes);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 0 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    refetch({
      search: searchTerm,
      page: newPage + 1, // API uses 1-based index
      limit: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 0 })); // Reset to first page
    refetch({
      search: searchTerm,
      page: 1,
      limit: newPageSize,
    });
  };

  const handleUserTypeChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const newUserTypes = typeof value === "string" ? value.split(",") : value;
    setSelectedUserTypes(newUserTypes);
    setPagination((prev) => ({ ...prev, page: 0 })); // Reset to first page
    refetch({
      userType: newUserTypes,
      businessClaimType,
      page: 1,
      limit: pagination.pageSize,
    });
  };

  const handleOpenModal = () => {
    setSendMessagesObj({
      ...initialSendMessagesObj,
      userList: selectedRows,
    });
    setOpenModal(true);
  };

  const userTypeLabels: Record<string, string> = {
    BUSINESS: "Business",
    USER: "User",
    CAMPAIGNUSER: "Campaign User",
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <>
      <Box p={2}>
        {!openModal && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              paddingRight={4}
            >
              <Typography variant="h4" style={{ color: "#00C5B9" }}>
                Messages
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <label style={{ marginRight: '8px' }}>
                    <input
                      type="radio"
                      name="claimStatus"
                      value="all"
                      style={{ marginRight: '5px' }}
                      checked={businessClaimType === undefined}
                      onChange={() => {
                        setBusinessClaimType(undefined);
                        refetch({
                          userType: selectedUserTypes,
                          businessClaimType: undefined,
                          page: 1,
                          limit: pagination.pageSize,
                        });
                      }}
                    />
                    All
                  </label>
                  <label style={{ marginRight: '8px' }}>
                    <input
                      type="radio"
                      name="claimStatus"
                      value="claimed"
                      style={{ marginRight: '5px' }}
                      checked={businessClaimType === true}
                      onChange={() => {
                        setBusinessClaimType(true);
                        refetch({
                          userType: selectedUserTypes,
                          businessClaimType: true,
                          page: 1,
                          limit: pagination.pageSize,
                        });
                      }}
                    />
                    Claimed
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="claimStatus"
                      value="notClaimed"
                      style={{ marginRight: '5px' }}
                      checked={businessClaimType === false}
                      onChange={() => {
                        setBusinessClaimType(false);
                        refetch({
                          userType: selectedUserTypes,
                          businessClaimType: false,
                          page: 1,
                          limit: pagination.pageSize,
                        });
                      }}
                    />
                    Not Claimed
                  </label>
                </Box>
                <Box width={350}>
                  <Select
                    multiple
                    displayEmpty
                    value={selectedUserTypes}
                    onChange={handleUserTypeChange}
                    size="small"
                    fullWidth
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "Select User Types"
                        : (selected as string[]).map((val) => userTypeLabels[val]).join(", ")
                    }
                  >
                    {Object.keys(userTypeLabels).map((type) => (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={selectedUserTypes.indexOf(type) > -1} />
                        <Typography variant="body2">{userTypeLabels[type]}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box width={200}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {hasPermissionPage("SendBulkMessages", "add") && (
                  <Box>
                    <CustomButton
                      onClick={handleOpenModal}
                      variant="contained"
                      className="width: 200px"
                      disabled={selectedRows?.length === 0}
                    >
                      Send
                    </CustomButton>
                  </Box>
                )}
                <Box>
                  <CustomButton
                    onClick={() => navigate(ROUTES.VIEW_MESSAGES)}
                    variant="contained"
                    className="width: 200px"
                  >
                    View Messages
                  </CustomButton>
                </Box>
              </Box>
            </Box>
            <Typography variant="h6">Selected Users ({selectedRows?.length})</Typography>
            <Box mt={2}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <TableCell key={column.id}>{column.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list?.length > 0 ? (
                      list?.map((row: any, index: number) => (
                        <TableRow key={index}>
                          {COLUMNS?.map((column) => (
                            <TableCell key={`${index}-${column.id}`}>
                              {column.render ? column.render(row) : row[column.id]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={COLUMNS.length}
                          className="text-center py-10"
                        >
                          <div style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            No data found
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[100, 1000, 2000, 4900]}
                        count={total}
                        rowsPerPage={pagination.pageSize}
                        page={pagination.page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handlePageSizeChange}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
        {openModal && (
          <SendBulkMessagesModal
            handleClose={handleCloseModal}
            refetchSendMessages={() => refetch()}
            initialSendMessagesObj={initialSendMessagesObj}
            sendMessagesObj={sendMessagesObj}
            setSendMessagesObj={setSendMessagesObj}
          />
        )}
      </Box>
    </>
  );
}

export default SendBulkMessages;